import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import SheetTop from "@coworker/apprestructured/src/task-management/components/SheetTop/SheetTop";
import CrossIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CrossIconPath";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import styles from "./CloseSheet.module.css";
import Texts from "@coworker/apprestructured/src/task-management/components/SheetTop/Texts/Texts";
import Subtitle from "@coworker/apprestructured/src/task-management/components/SheetTop/Subtitle/Subtitle";
import CloseButtonWrapper from "@coworker/apprestructured/src/task-management/components/SheetTop/CloseButtonWrapper/CloseButtonWrapper";
import Title from "@coworker/apprestructured/src/task-management/components/SheetTop/Title/Title";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import CloseReasonListItem from "@coworker/apprestructured/src/task-management/components/CloseSheet/CloseReasonListItem/CloseReasonListItem";
import FixaHelperText from "@coworker/apprestructured/src/shared/wrappers/FixaHelperText/FixaHelperText";
import InputWrapper from "@coworker/apprestructured/src/task-management/components/InputWrapper/InputWrapper";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import ModalColumn from "../ModalColumn/ModalColumn";
import { useTranslation } from "@coworker/locales";

export type CloseReasonOption = {
  value: string;
  label: string;
  requiresDescription: boolean;
};

type CloseSheetProps = {
  options: CloseReasonOption[];
  closeReasonId: string | null;
  onCloseReasonIdChange: (newCloseReasonId: string | null) => void;
  description: string;
  onDescriptionChange: (newDescription: string) => void;
  onCloseTask: () => void;
  onClose: () => void;
  visible: boolean;
};

function CloseSheet({
  options,
  closeReasonId,
  onCloseReasonIdChange,
  description,
  onDescriptionChange,
  onCloseTask,
  onClose,
  visible,
}: CloseSheetProps) {
  const [showErrors, setShowErrors] = useState(false);

  const { t } = useTranslation();

  const reasonError = useMemo(() => {
    if (!closeReasonId) {
      return t("pleaseSelectAReasonString");
    }
    return null;
  }, [closeReasonId, t]);

  const descriptionError = useMemo(() => {
    if (closeReasonId && description.length === 0) {
      const option = options.find(({ value }) => value === closeReasonId)!;
      if (option.requiresDescription) {
        return t("pleaseFillInString");
      }
    }
    return null;
  }, [closeReasonId, description.length, options, t]);

  const handleCloseTask = useCallback(() => {
    if (reasonError || descriptionError) {
      setShowErrors(true);
    } else {
      onCloseTask();
    }
  }, [descriptionError, onCloseTask, reasonError]);

  const header = useMemo(() => {
    return (
      <SheetTop className={styles["sheetTop"]}>
        <CloseButtonWrapper>
          <FixaButton
            onClick={onClose}
            ssrIcon={CrossIconPath}
            iconOnly
            type="tertiary"
            size="small"
          />
        </CloseButtonWrapper>
        <Texts>
          <Title>{t("closeString")}</Title>
          <Subtitle>{t("selectCloseReasonString")}</Subtitle>
        </Texts>
      </SheetTop>
    );
  }, [onClose, t]);

  const footer = useMemo(() => {
    return (
      <StyledModalFooter>
        <FixaButton onClick={handleCloseTask} type="primary" size="small">
          {t("closeString")}
        </FixaButton>
      </StyledModalFooter>
    );
  }, [handleCloseTask, t]);

  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onDescriptionChange(e.target.value);
    },
    [onDescriptionChange]
  );

  const handleSelectCloseReasonId = useCallback(
    (newCloseReasonId: string) => {
      onCloseReasonIdChange(newCloseReasonId);
    },
    [onCloseReasonIdChange]
  );

  return (
    <FixaModal visible={visible} handleCloseBtn={onClose}>
      <FixaSheets header={header} footer={footer} fullSize>
        <FixaModalBody>
          <ModalColumn gap="medium">
            <ModalColumn gap="small">
              <FixaListView id="close_reasons" size="small">
                {options?.map(({ value, label }) => {
                  return (
                    <CloseReasonListItem
                      key={value}
                      value={value}
                      title={label}
                      checked={value === closeReasonId}
                      onSelect={handleSelectCloseReasonId}
                    />
                  );
                })}
              </FixaListView>
              {showErrors && reasonError && (
                <FixaHelperText
                  className={styles["error"]!}
                  shouldValidate={true}
                >
                  {reasonError}
                </FixaHelperText>
              )}
            </ModalColumn>
            <InputWrapper error={showErrors ? descriptionError : null}>
              <FixaTextArea
                value={description}
                label={t("description4String")}
                onChange={handleDescriptionChange}
              />
            </InputWrapper>
          </ModalColumn>
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
}

export default CloseSheet;
